/**
 * Generic BrowserBroadcastChannelService
 *
 * Provides communication between browser tabs/windows using BroadcastChannel API
 * with fallback to localStorage for unsupported browsers. Implements leader election
 * algorithm to coordinate actions between tabs.
 *
 * Key Features:
 * - Inter-tab communication via BroadcastChannel API
 * - Leader election and heartbeat system
 * - Generic pub/sub messaging system
 * - Automatic fallback to localStorage for older browsers
 * - Tab lifecycle management (visibility changes, beforeunload)
 *
 * Leader Election:
 * The service automatically elects one tab as "leader" to coordinate actions
 * that should only happen once (like showing desktop notifications).
 * Leadership is transferred when:
 * - Current leader becomes hidden/inactive
 * - A visible tab becomes available
 * - Leader tab is closed
 *
 * Usage:
 * ```javascript
 * const service = BrowserBroadcastChannelService.getInstance()
 *
 * // Subscribe to messages
 * service.subscribe('MY_EVENT', (data) => {
 *   console.log('Received:', data)
 * })
 *
 * // Broadcast messages
 * service.broadcast('MY_EVENT', { message: 'Hello from tab!' })
 * ```
 *
 * @class BrowserBroadcastChannelService
 * @since 2024
 * @author eContainers Team
 */
class BrowserBroadcastChannelService {
  constructor() {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') {
      // SSR-safe initialization - create a minimal, non-functional instance
      this.storageKey = 'browser_channel_leader'
      this.tabId = 'ssr-tab-' + Date.now()
      this.useBroadcastChannel = false
      this.heartbeatInterval = null
      this.isLeader = false
      this.leaderTimeout = null
      this.lastHeartbeat = Date.now()
      this.isChannelClosed = true
      this.leaderCheckTimeout = null
      this.messageSubscriptions = new Map()
      return
    }

    this.storageKey = 'browser_channel_leader'
    this.tabId = this.generateTabId()
    this.useBroadcastChannel = 'BroadcastChannel' in window
    this.heartbeatInterval = null
    this.isLeader = false
    this.leaderTimeout = null
    this.lastHeartbeat = Date.now()
    this.isChannelClosed = false
    this.leaderCheckTimeout = null

    // Generic subscription system
    this.messageSubscriptions = new Map()

    this.initialize()
  }

  /**
   * Generate unique identifier for this tab
   * Uses crypto.randomUUID() when available, fallback to timestamp + random string
   * @returns {string} Unique tab identifier
   */
  generateTabId() {
    // SSR-safe check
    if (typeof window === 'undefined') {
      return (
        'ssr-tab-' +
        Date.now() +
        '-' +
        Math.random()
          .toString(36)
          .substr(2, 9)
      )
    }

    if ('crypto' in window && 'randomUUID' in crypto) {
      return crypto.randomUUID()
    }
    const timestamp = performance.now ? performance.now() : Date.now()
    const random = Math.random()
      .toString(36)
      .substr(2, 9)
    return `${timestamp}-${random}`
  }

  /**
   * Initialize the service - sets up BroadcastChannel or localStorage fallback
   * Starts leader election process and registers event listeners
   */
  initialize() {
    // Skip initialization in SSR environment
    if (typeof window === 'undefined') {
      return
    }

    if (this.useBroadcastChannel) {
      this.channel = new BroadcastChannel('app-coordination')
      this.channel.addEventListener('message', this.handleMessage.bind(this))

      queueMicrotask(() => {
        this.tryBecomeLeader()
      })

      this.startHeartbeat()
    } else {
      window.addEventListener('storage', this.handleStorageChange.bind(this))
      localStorage.removeItem(this.storageKey)
    }

    document.addEventListener(
      'visibilitychange',
      this.handleVisibilityChange.bind(this)
    )
    window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this))
  }

  /**
   * Handle localStorage changes for older browser fallback
   * Notifies subscribers and manages leader election via localStorage
   * @param {StorageEvent} event - Storage event from localStorage
   */
  handleStorageChange(event) {
    if (event.key === this.storageKey) {
      this.lastHeartbeat = Date.now()

      try {
        const data = JSON.parse(event.newValue || '{}')
        this.notifySubscribers('STORAGE_CHANGE', data)
        if (data.tabId && data.tabId !== this.tabId) {
          this.stopBeingLeader()
        }
      } catch (error) {}
    }
  }

  /**
   * Generic broadcast method
   * @param {string} type - Message type
   * @param {Object} data - Message data
   */
  broadcast(type, data) {
    // Skip in SSR environment
    if (typeof window === 'undefined' || this.isChannelClosed) {
      return
    }

    if (this.channel && !this.isChannelClosed) {
      try {
        this.channel.postMessage({
          type,
          data: {
            ...data,
            timestamp: Date.now()
          }
        })
      } catch (error) {
        if (error.name === 'InvalidStateError') {
          this.isChannelClosed = true
        }
      }
    }
  }

  // Legacy method for backwards compatibility
  sendMessage(type, data) {
    this.broadcast(type, data)
  }

  /**
   * Attempt to become the leader tab
   * Sends a LEADER_CHECK message and waits for responses from existing leaders
   * If no leader responds within 200ms, this tab becomes the leader
   */
  tryBecomeLeader() {
    if (typeof window === 'undefined' || this.isChannelClosed) return

    this.sendMessage('LEADER_CHECK', { tabId: this.tabId })

    this.leaderCheckTimeout = Promise.resolve()
      .then(() => {
        return new Promise(resolve => {
          setTimeout(resolve, 200)
        })
      })
      .then(() => {
        if (!this.isLeader && !this.isChannelClosed) {
          this.becomeLeader()
        }
      })
  }

  /**
   * Make this tab the leader
   * Announces leadership to other tabs and notifies subscribers
   */
  becomeLeader() {
    if (!this.isLeader && !this.isChannelClosed) {
      this.isLeader = true
      this.sendMessage('LEADER_ANNOUNCEMENT', { tabId: this.tabId })
      this.notifySubscribers('LEADER_CHANGED', {
        isLeader: true,
        tabId: this.tabId
      })
    }
  }

  /**
   * Stop being the leader tab
   * Cleans up leader-related resources and notifies subscribers
   */
  stopBeingLeader() {
    if (this.isLeader) {
      this.isLeader = false
      if (this.leaderTimeout) {
        clearTimeout(this.leaderTimeout)
        this.leaderTimeout = null
      }
      this.notifySubscribers('LEADER_CHANGED', {
        isLeader: false,
        tabId: this.tabId
      })
    }
  }

  /**
   * Start the heartbeat system for leader election
   * Leaders send heartbeats every 2 seconds, followers check for missing heartbeats
   */
  startHeartbeat() {
    // Skip in SSR environment
    if (typeof window === 'undefined') {
      return
    }

    this.heartbeatInterval = setInterval(() => {
      if (this.isChannelClosed) {
        clearInterval(this.heartbeatInterval)
        return
      }

      if (this.isLeader) {
        this.sendMessage('LEADER_HEARTBEAT', {
          tabId: this.tabId,
          isHidden: document.hidden
        })
      } else {
        const now = Date.now()
        if (now - this.lastHeartbeat > 6000) {
          this.tryBecomeLeader()
        }
      }
    }, 2000)
  }

  /**
   * Subscribe to specific message types
   * @param {string} type - Message type to subscribe to
   * @param {Function} callback - Callback function
   */
  subscribe(type, callback) {
    if (!this.messageSubscriptions.has(type)) {
      this.messageSubscriptions.set(type, new Set())
    }
    this.messageSubscriptions.get(type).add(callback)
  }

  /**
   * Unsubscribe from message type
   * @param {string} type - Message type
   * @param {Function} callback - Callback function to remove
   */
  unsubscribe(type, callback) {
    const callbacks = this.messageSubscriptions.get(type)
    if (callbacks) {
      callbacks.delete(callback)
      if (callbacks.size === 0) {
        this.messageSubscriptions.delete(type)
      }
    }
  }

  /**
   * Notify subscribers of a message
   * @param {string} type - Message type
   * @param {Object} data - Message data
   */
  notifySubscribers(type, data) {
    const callbacks = this.messageSubscriptions.get(type)
    if (callbacks) {
      for (const callback of callbacks) {
        try {
          callback(data)
        } catch (error) {
          console.error(`Error in ${type} subscriber:`, error)
        }
      }
    }
  }

  /**
   * Handle incoming messages from BroadcastChannel
   * Notifies subscribers and processes internal coordination messages
   * @param {MessageEvent} event - Message event from BroadcastChannel
   */
  handleMessage(event) {
    const { type, data } = event.data

    // Notify subscribers of all messages
    this.notifySubscribers(type, data)

    // Handle internal coordination messages
    switch (type) {
      case 'LEADER_CHECK':
        if (this.isLeader && data.tabId !== this.tabId) {
          this.sendMessage('LEADER_EXISTS', {
            tabId: this.tabId,
            isHidden: document.hidden
          })
        }
        break

      case 'LEADER_EXISTS':
        if (data.tabId !== this.tabId) {
          this.stopBeingLeader()
          this.lastHeartbeat = Date.now()

          if (!document.hidden && data.isHidden) {
            queueMicrotask(() => {
              this.becomeLeader()
            })
          }
        }
        break

      case 'LEADER_ANNOUNCEMENT':
        if (data.tabId !== this.tabId) {
          this.stopBeingLeader()
          this.lastHeartbeat = Date.now()
        }
        break

      case 'LEADER_HEARTBEAT':
        if (data.tabId !== this.tabId) {
          this.lastHeartbeat = Date.now()

          if (this.isLeader) {
            this.stopBeingLeader()
          }

          if (!document.hidden && data.isHidden && !this.isLeader) {
            queueMicrotask(() => {
              this.becomeLeader()
            })
          }
        }
        break

      case 'TAB_BECAME_VISIBLE':
        if (data.tabId !== this.tabId && this.isLeader && document.hidden) {
          this.stopBeingLeader()
        }
        break

      case 'LEADER_LEAVING':
        if (data.tabId !== this.tabId) {
          this.lastHeartbeat = 0
          queueMicrotask(() => {
            this.tryBecomeLeader()
          })
        }
        break
    }
  }

  /**
   * Handle tab visibility changes
   * When tab becomes visible, it may attempt to become leader
   * for better user experience (visible tabs are preferred as leaders)
   */
  handleVisibilityChange() {
    // Skip in SSR environment
    if (typeof window === 'undefined') {
      return
    }

    if (
      document.visibilityState === 'visible' &&
      this.useBroadcastChannel &&
      !this.isChannelClosed
    ) {
      this.sendMessage('TAB_BECAME_VISIBLE', { tabId: this.tabId })

      if (!this.isLeader) {
        this.lastHeartbeat = Date.now() - 3000
        queueMicrotask(() => {
          if (!this.isLeader) {
            this.tryBecomeLeader()
          }
        })
      }
    }
  }

  /**
   * Handle tab closing/refreshing
   * Cleans up resources and notifies other tabs if this tab was the leader
   */
  handleBeforeUnload() {
    this.isChannelClosed = true

    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }

    if (this.leaderTimeout) {
      clearTimeout(this.leaderTimeout)
      this.leaderTimeout = null
    }

    if (this.isLeader) {
      this.sendMessage('LEADER_LEAVING', { tabId: this.tabId })
    }

    // Skip localStorage operations in SSR environment
    if (typeof window !== 'undefined' && !this.useBroadcastChannel) {
      localStorage.removeItem(this.storageKey)
    }
  }

  /**
   * Clean up all resources and close the service
   * Should be called when the application is shutting down
   */
  destroy() {
    this.isChannelClosed = true
    this.handleBeforeUnload()

    if (this.channel) {
      try {
        this.channel.close()
      } catch (error) {}
    }

    this.messageSubscriptions.clear()

    if (this.leaderTimeout) {
      clearTimeout(this.leaderTimeout)
    }
  }

  /**
   * Get singleton instance of the service
   * Ensures only one instance exists per page to coordinate properly
   * @returns {BrowserBroadcastChannelService} The singleton instance
   */
  static getInstance() {
    if (!BrowserBroadcastChannelService.instance) {
      BrowserBroadcastChannelService.instance = new BrowserBroadcastChannelService()
    }
    return BrowserBroadcastChannelService.instance
  }
}

export default BrowserBroadcastChannelService
